import type { ClerkOrgRole } from './claims'

/** Authority admitted at a server boundary; attribution is a separate concern. */
export type ExecutionPrincipal =
  | Readonly<{
      kind: 'human'
      orgId: string
      userId: string
      membershipId: string
      orgRole: ClerkOrgRole
    }>
  | Readonly<{ kind: 'org_api_key'; orgId: string; keyId: string }>
  | Readonly<{
      kind: 'personal_oauth'
      orgId: string
      userId: string
      membershipId: string
      tokenId: string
    }>
  | ChannelUserPrincipal
  | Readonly<{
      kind: 'internal_job'
      orgId: string
      brandId: string
      purpose: Exclude<
        InternalJobPurpose,
        'brand_setup' | 'domain_verification'
      >
    }>
  | BrandSetupPrincipal
  | DomainVerificationPrincipal

/**
 * Chat apps a person can talk to Brew through. One adapter per kind
 * (`lib/channels/`); every validator of a kind derives from this list.
 */
export const CHANNEL_KINDS = ['slack'] as const

export type ChannelKind = (typeof CHANNEL_KINDS)[number]

/** A provider id that names a chat app (one of `CHANNEL_KINDS`). */
export function isChannelKind(value: string): value is ChannelKind {
  return (CHANNEL_KINDS as ReadonlyArray<string>).includes(value)
}

/** Each chat app's name as people see it; a new kind needs one to compile. */
export const CHANNEL_KIND_LABEL: Readonly<Record<ChannelKind, string>> = {
  slack: 'Slack',
}

/**
 * A person acting through a chat app they linked to their Brew user. The
 * stored link row (`channelIdentities`) is the credential: Convex rechecks it,
 * the workspace install, and the person's live membership on every call. It
 * reaches the brands the person can open and never carries organization
 * control.
 */
export type ChannelUserPrincipal = Readonly<{
  kind: 'channel_user'
  orgId: string
  userId: string
  membershipId: string
  channel: ChannelKind
  identityId: string
}>

/** The service continuation retains the provider binding admitted by its signed setup receipt. */
export type DomainVerificationPrincipal = Readonly<{
  kind: 'internal_job'
  purpose: 'domain_verification'
  orgId: string
  brandId: string
  domainId: string
  externalId: string
}>

export type BrandSetupPrincipal = Readonly<{
  kind: 'internal_job'
  orgId: string
  brandId: string
  purpose: 'brand_setup'
  generation: string
}>

export type InitiatingExecutionPrincipal = Exclude<
  ExecutionPrincipal,
  { kind: 'internal_job' }
>

export type InternalJobPurpose =
  | 'brand_setup'
  | 'published_automation'
  | 'campaign_send'
  | 'integration_ingest'
  | 'api_trigger_fire'
  | 'domain_score'
  | 'domain_verification'
  | 'inbox_placement'
  | 'brand_extraction'
  | 'eval'
